import { useCallback, useRef, useState } from 'react'

interface IUseGuardedLoadMoreParams {
  /** Quantos itens já foram carregados — é o offset da próxima página. */
  offset: number
  hasMore: boolean
  onLoadMore: () => Promise<void>
  onError?: (error: unknown) => void
}

/**
 * Envolve o `onLoadMore` de uma lista paginada com duas guardas:
 * concorrência (uma request por vez) e offset já pedido.
 *
 * A sentinela de fim de lista pode chamar várias vezes para a mesma página (nó
 * re-observado, scroll rápido) e o offset fica stale entre o fim da request e o
 * re-render — sem as guardas isso vira request duplicada e página repetida.
 * Offset é liberado em caso de erro para permitir nova tentativa.
 */
export function useGuardedLoadMore({
  offset,
  hasMore,
  onLoadMore,
  onError,
}: IUseGuardedLoadMoreParams) {
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const isLoadingMoreRef = useRef(false)
  const requestedOffsetsRef = useRef<Set<number>>(new Set())

  const loadMore = useCallback(async () => {
    if (!hasMore) return
    if (isLoadingMoreRef.current) return
    if (requestedOffsetsRef.current.has(offset)) return

    isLoadingMoreRef.current = true
    requestedOffsetsRef.current.add(offset)
    setIsLoadingMore(true)

    try {
      await onLoadMore()
    } catch (error) {
      requestedOffsetsRef.current.delete(offset)
      onError?.(error)
    } finally {
      isLoadingMoreRef.current = false
      setIsLoadingMore(false)
    }
  }, [hasMore, offset, onLoadMore, onError])

  return { loadMore, isLoadingMore }
}
